/**
 * SearchableDropdown Component
 * A reusable searchable dropdown component with typeahead functionality
 */

import React, { useState, useEffect, useRef } from 'react';

export interface SearchableDropdownProps<T> {
  label?: string;
  value: string;
  onChange: (val: T) => void;
  onSearchChange?: (searchText: string) => void;
  options: T[];
  placeholder?: string;
  renderOption?: (opt: T) => React.ReactNode;
  filterFn?: (opt: T, searchText: string) => boolean;
  getDisplayValue?: (opt: T) => string;
  /** Return false to prevent dropdown from closing after selection */
  shouldCloseOnSelect?: (opt: T) => boolean;
  /** Content rendered at the top of the dropdown panel (inside the ref, so clicks don't close it) */
  dropdownHeader?: React.ReactNode;
}

export function SearchableDropdown<T>({
  label,
  value,
  onChange,
  onSearchChange,
  options,
  placeholder,
  renderOption,
  filterFn,
  getDisplayValue,
  shouldCloseOnSelect,
  dropdownHeader
}: SearchableDropdownProps<T>) {
  const [searchText, setSearchText] = useState(value || '');
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setSearchText(value || '');
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredOptions = filterFn ? options.filter(opt => filterFn(opt, searchText)) : options;

  return (
    <div className="relative" ref={dropdownRef}>
      {label && <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>}
      <input
        ref={inputRef}
        type="text"
        value={searchText}
        onChange={(e) => {
          const newValue = e.target.value;
          setSearchText(newValue);
          setShowDropdown(true);
          if (onSearchChange) {
            onSearchChange(newValue);
          }
        }}
        onFocus={() => setShowDropdown(true)}
        placeholder={placeholder}
        className="w-full px-3 py-2 border border-gray-200 rounded focus:outline-none focus:border-green-400 focus:ring-1 focus:ring-green-400"
      />
      {showDropdown && (
        <div
          className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
          onMouseDown={(e) => e.preventDefault()}
        >
          {dropdownHeader}
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => (
              <div
                key={index}
                onClick={(e) => {
                  e.stopPropagation();
                  const close = shouldCloseOnSelect ? shouldCloseOnSelect(option) : true;
                  onChange(option);
                  if (close) {
                    setSearchText(getDisplayValue ? getDisplayValue(option) : (typeof option === 'string' ? option : (option as any).id));
                    setShowDropdown(false);
                  }
                }}
                className="w-full text-left px-3 py-2 hover:bg-green-50 transition-colors border-b border-gray-100 last:border-b-0 cursor-pointer"
              >
                {renderOption ? renderOption(option) : option}
              </div>
            ))
          ) : (
            <div className="px-3 py-2 text-sm text-gray-500">No results found</div>
          )}
        </div>
      )}
    </div>
  );
}
